/**
 * V4 scenarios live alongside V3 in the same UI. We keep the V3 Immutable
 * `Scenario` model as the single shape consumers see and tag V4-sourced
 * records with `source: 'v4'` + the raw V4 payload on `v4Data`. V3↔V4 dedup
 * (for scenarios that exist in both) is done by the saga via
 * `migrationLookupDetail('scenario_<v3-id>')`.
 */

import cloneDeep from 'lodash/cloneDeep';
import V4API from '../../api/V4client';
import { mapV4ScenarioToV3 } from './transforms';
import {
    getV4ActionTypes,
    getV4TriggerSources,
    isKnownV4ActionType,
    isKnownV4TriggerSource,
} from './dictionaries';
import { toV4ActionType, toV4TriggerSource } from './translations';
import { toV4ActionConfigs, unresolvedRecipientIds } from './actionConfigs';
import { EDITOR_FIELDS, parseV4FieldError } from './fieldErrors';
import { referencedV3Ids, toIdArray, translateIds } from './ids';
import { triggerConfigs, triggerConfigKeys } from '../../utils/models/scenarios/triggerConfigs';
import { forAnyObject } from '../../utils/models/Scenario';
import {
    DEFAULT_V4_SCHEDULE,
    encodeV4Schedule,
    isValidScheduleGrid,
    isValidScheduleMasks,
} from './schedule';

const getField = (obj, key) =>
    obj && typeof obj.get === 'function' ? obj.get(key) : obj?.[key];

// `scope` pins the call to another account; omit it and V4client scopes the
// request to the session's own (see resolveV4Scope).
export async function fetchV4Scenarios(scope, { idMaps = {}, timezone } = {}) {
    try {
        // SDK defaults to limit=20; page through with a generous size.
        const pageSize = 200;
        let offset = 0;
        const all = [];
        for (let i = 0; i < 50; i += 1) {
            const query = { limit: pageSize, offset };
            const res = await V4API.scenariosList(query, scope);
            const batch = res?.data || [];
            all.push(...batch);
            if (batch.length < pageSize) break;
            offset += batch.length;
        }
        return all.map(s => mapV4ScenarioToV3(s, { idMaps, timezone }));
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch V4 scenarios', e?.response?.data || e);
        return [];
    }
}

export async function fetchV4Scenario(scenarioId, scope) {
    const res = await V4API.scenariosDetail(scenarioId, scope);
    return res?.data;
}

export async function executeV4Scenario(scenarioId, scope) {
    await V4API.scenariosExecuteCreate(scenarioId, scope);
}

/**
 * Translate a V3-shaped Scenario into a V4 CreateScenarioRequest /
 * UpdateScenarioRequest body. Untouched complex fields fall back to
 * `v4Data` so V3-driven saves don't clobber V4-only shape. The caller
 * pre-resolves V3 numeric ids to V4 UUIDs in `idMaps`.
 */
export function toV4ScenarioRequest(scenario, { idMaps = {}, timezone } = {}) {
    const v4Data = getField(scenario, 'v4Data') || {};
    const name = getField(scenario, 'name') || '';
    const enabled = getField(scenario, 'enabled');
    const v3Trigger = getField(scenario, 'trigger');
    const v3TriggerConfig = getField(scenario, 'triggerConfig');
    const v3Actions = getField(scenario, 'actions');

    // `saveV4Scenario` treats a missing/zero id as a create; keep the two in step.
    const isExisting = !!getField(scenario, 'id');

    const triggers = projectTriggers(v3Trigger, v3TriggerConfig, v4Data, idMaps, isExisting);
    const actions = projectActions(v3Actions, v4Data, idMaps);

    validateAgainstV4Dictionaries(triggers, actions);

    return {
        name,
        description: v4Data.description || '',
        is_active: enabled !== false,
        schedule: resolveV4Schedule(scenario, v4Data, timezone),
        triggers,
        conditions: Array.isArray(v4Data.conditions) ? v4Data.conditions : [],
        actions,
    };
}

// Encode the editor's local boolean[7][48] grid to V4's UTC bitmasks. Fall back
// to the raw V4 schedule — already UTC, so unshifted (grid never opened) — then
// the "always" default.
function resolveV4Schedule(scenario, v4Data, timezone) {
    const grid = getField(scenario, 'schedule');
    if (isValidScheduleGrid(grid)) return encodeV4Schedule(grid, timezone);
    if (isValidScheduleMasks(v4Data.schedule)) return v4Data.schedule;
    return DEFAULT_V4_SCHEDULE;
}

// V4 rejects unknown sources/types with a generic `invalid_request_body`;
// fail fast here with the valid options so the snackbar is actionable.
function validateAgainstV4Dictionaries(triggers, actions) {
    for (const trigger of triggers) {
        if (trigger?.source && !isKnownV4TriggerSource(trigger.source)) {
            const valid = getV4TriggerSources().join(', ') || '(dictionary empty)';
            throw new Error(
                `trigger source "${trigger.source}" is not accepted by V4. Valid sources: ${valid}`
            );
        }
    }
    for (const action of actions) {
        if (action?.type && !isKnownV4ActionType(action.type)) {
            const valid = getV4ActionTypes().join(', ') || '(dictionary empty)';
            throw new Error(
                `action type "${action.type}" is not accepted by V4. Valid types: ${valid}`
            );
        }
    }
}

// Keep emitting the pre-migration flat id arrays until every environment is on
// the nested shape. Flip to false to retire them.
const WRITE_LEGACY_TRIGGER_FIELDS = true;

// Returns null when there is nothing to say, so an unselected entity is omitted
// rather than sent as an ambiguous {}.
function buildEntityFilter(rawIds, map, { allowAny = false } = {}) {
    const filter = {};
    const include = translateIds(rawIds, map);
    if (include.length) filter.include = include;
    if (allowAny && toIdArray(rawIds).some(id => Number(id) === forAnyObject)) {
        filter.any = true;
    }
    return Object.keys(filter).length ? filter : null;
}

function projectTriggers(v3Trigger, v3TriggerConfig, v4Data, idMaps, isExisting) {
    if (!v3Trigger) return Array.isArray(v4Data.triggers) ? v4Data.triggers : [];
    const cfg = v3TriggerConfig || {};
    // The geofences category's V3 config is a bare id array, not an object.
    const isBareGeofenceConfig =
        triggerConfigs[v3Trigger] === triggerConfigKeys.geofences && Array.isArray(v3TriggerConfig);
    const geofenceIds = isBareGeofenceConfig ? v3TriggerConfig : cfg.geofenceIds;

    const devices = buildEntityFilter(cfg.deviceIds, idMaps.deviceIdToUuid, { allowAny: true });
    const teams = buildEntityFilter(cfg.teamIds, idMaps.teamIdToUuid, { allowAny: true });
    const geofences = buildEntityFilter(geofenceIds, idMaps.geofenceIdToUuid, { allowAny: true });

    const trigger = { source: toV4TriggerSource(v3Trigger) };
    if (devices) trigger.devices = devices;
    if (teams) trigger.teams = teams;
    if (geofences) trigger.geofences = geofences;

    const previous = Array.isArray(v4Data.triggers) ? v4Data.triggers[0] : null;
    const sameSource = !!previous && previous.source === trigger.source;

    // V3 has one inverted switch; it belongs to whichever source is active.
    // Attendance configs carry no `source`, so use the one filter they produced.
    if (cfg.inverted) {
        const target = cfg.source || (teams ? 'teams' : geofences ? 'geofences' : 'devices');
        if (trigger[target]) trigger[target].inverted = true;
    }
    // A bare geofence config has nowhere to hold `inverted`, so carry the stored
    // flag rather than clearing it on every save, rename included.
    if (sameSource && isBareGeofenceConfig && trigger.geofences && previous.geofences?.inverted) {
        trigger.geofences.inverted = true;
    }

    // `code` has no V3 editor home, so don't destroy one V4 stored. Gated on the
    // source being unchanged, or a trigger swap smuggles it across, and on the
    // scenario existing: `createCopy` carries `v4Data`, so a copy would
    // otherwise POST claiming the original's inbound code.
    if (isExisting && sameSource && previous.code) trigger.code = previous.code;

    // The dashboard toggle skips `canSave`: omitting a `concerning`-only filter would widen it.
    if (sameSource && !devices && !teams && !geofences && previous.devices?.concerning) {
        trigger.devices = previous.devices;
    }

    // Pre-migration spelling, sent only when it carries the same ids as the
    // nested filter — never as an empty array that could out-vote it.
    if (WRITE_LEGACY_TRIGGER_FIELDS) {
        if (devices?.include) trigger.device_ids = devices.include;
        if (teams?.include) trigger.team_ids = teams.include;
        if (geofences?.include) trigger.geofence_ids = geofences.include;
        trigger.inverted = !!cfg.inverted;
    }

    return [trigger];
}

const countByType = (items, typeOf) => {
    const counts = {};
    items.forEach(item => {
        const type = typeOf(item);
        counts[type] = (counts[type] || 0) + 1;
    });
    return counts;
};

/**
 * The V4 config an action was loaded from, for the fields V4 declares but the
 * V3 editor can't show. Answers only when the type appears exactly once on each
 * side: position can't identify an action (one V3 action may produce several V4
 * ones), and pairing two email actions by order would move one's stored HTML
 * body onto the other's recipients — worse than dropping it.
 */
function makeStoredConfigReader(v3Types, v4Data) {
    const stored = Array.isArray(v4Data.actions) ? v4Data.actions : [];
    const storedCounts = countByType(stored, a => a?.type);
    const v3Counts = countByType(v3Types, t => t);
    return type => {
        if (storedCounts[type] !== 1 || v3Counts[type] !== 1) return undefined;
        return stored.find(a => a?.type === type)?.config;
    };
}

const actionList = raw => (Array.isArray(raw) ? raw : raw?.toArray?.() || []);

// Whole-list context, so a test carries stored configs over as a save does.
function makeActionProjector(list, v4Data, idMaps) {
    const types = list.map(a => toV4ActionType(getField(a, 'type') || ''));
    const storedConfigFor = makeStoredConfigReader(types, v4Data);
    return index => {
        const type = types[index];
        const previous = storedConfigFor(type);
        const configs = toV4ActionConfigs(type, getField(list[index], 'config'), { idMaps, previous });
        return configs.map(config => ({ type, config }));
    };
}

// Flattens: `change_team` takes one team per action where the editor shows a list.
function projectActions(v3Actions, v4Data, idMaps) {
    const list = actionList(v3Actions);
    if (!list.length) return Array.isArray(v4Data.actions) ? v4Data.actions : [];
    const project = makeActionProjector(list, v4Data, idMaps);
    const out = [];
    list.forEach((_, index) => out.push(...project(index)));
    return out;
}

// What a save would send for this one action; its siblings aren't translated.
export function toV4TestActions(scenario, actionIndex, { idMaps = {} } = {}) {
    const list = actionList(getField(scenario, 'actions'));
    if (!list[actionIndex]) return [];
    const v4Data = getField(scenario, 'v4Data') || {};
    const actions = makeActionProjector(list, v4Data, idMaps)(actionIndex);
    validateAgainstV4Dictionaries([], actions);
    return actions;
}

// Editor action index per V4 action; change_team sends one per team.
export function v4ActionSources(scenario, { idMaps = {} } = {}) {
    const list = actionList(getField(scenario, 'actions'));
    const project = makeActionProjector(list, getField(scenario, 'v4Data') || {}, idMaps);
    return list.flatMap((_, index) => project(index).map(() => index));
}

// e.g. `actions[2].config.recipients[0]_email_invalid`
const ACTION_ERROR = /^actions\[(\d+)\][._]?(?:config\.)?(.*)$/;

// A contact goes out as its address, so V4's index can't address the editor's list.
function refusedRecipients(sent, item, config, idMaps) {
    const recipients = Array.isArray(config.recipients) ? config.recipients : [];
    const address = sent.config?.recipients?.[item];
    return recipients.filter(recipient =>
        toV4ActionConfigs(sent.type, { recipients: [recipient] }, { idMaps })[0].recipients[0] === address
    );
}

function toActionError(text, sent, action, idMaps) {
    const error = parseV4FieldError(text);
    const editorField = EDITOR_FIELDS[sent?.type]?.[error.field];
    if (!editorField) return error;
    const config = getField(action, 'config') || {};
    return {
        ...error,
        editorField,
        // Editors mutate their config in place.
        value: cloneDeep(config[editorField]),
        ...(error.item === undefined ? {} : { refused: refusedRecipients(sent, error.item, config, idMaps) }),
    };
}

export function v4SaveErrors(e, scenario, { body, sources, idMaps } = {}) {
    const out = { actionErrors: {}, nameError: null };
    const errors = e?.response?.data?.validation_errors;
    if (!Array.isArray(errors)) return out;
    const actions = actionList(getField(scenario, 'actions'));
    errors.map(String).forEach(text => {
        const match = ACTION_ERROR.exec(text);
        if (!match) {
            const error = parseV4FieldError(text);
            if (error.field === 'name') out.nameError = { ...error, value: getField(scenario, 'name') };
            return;
        }
        const index = sources?.[+match[1]];
        if (index === undefined) return;
        (out.actionErrors[index] = out.actionErrors[index] || []).push(
            toActionError(match[2] || text, body?.actions?.[+match[1]], actions[index], idMaps)
        );
    });
    return out;
}

export async function saveV4Scenario(scenario, body, scope) {
    const id = getField(scenario, 'id');
    const isNew = !id || id === 0;
    if (!isNew) {
        const res = await V4API.scenariosUpdate(id, body, scope);
        return res?.data;
    }
    const res = await V4API.scenariosCreate(body, scope);
    return res?.data;
}

export async function deleteV4Scenario(scenario, scope) {
    const id = getField(scenario, 'id');
    if (!id) return;
    await V4API.scenariosDelete(id, scope);
}

// Unlike V3's /sec/scenario/test, V4 tests against the stored scenario.
export async function testV4ScenarioAction(scenario, action, scope) {
    const id = getField(scenario, 'id');
    if (!id) throw new Error('save the scenario before testing its actions');
    const res = await V4API.scenariosActionsTestCreate(id, { action }, scope);
    return res?.data;
}

// Numeric V3 device ids referenced by a scenario's trigger config — used by
// the saga to resolve V3→V4 UUIDs before saving.
export function getReferencedV3DeviceIds(scenario) {
    const cfg = getField(scenario, 'triggerConfig') || {};
    return referencedV3Ids(cfg.deviceIds);
}

const uniqueIds = ids => Array.from(new Set(ids));

/**
 * Numeric V3 ids referenced by the *actions*, per entity type. Kept apart from
 * the trigger's device ids, which skip `ensureV4DeviceUuid`'s type gate.
 */
export function getReferencedV3ActionIds(scenario) {
    const raw = getField(scenario, 'actions');
    const list = Array.isArray(raw) ? raw : raw?.toArray?.() || [];
    const deviceIds = [];
    const teamIds = [];
    const geofenceIds = [];
    const scenarioIds = [];
    for (const action of list) {
        const cfg = getField(action, 'config') || {};
        const recipients = cfg.recipients || {};
        deviceIds.push(...referencedV3Ids(recipients.devices));
        teamIds.push(...referencedV3Ids(recipients.teams));
        teamIds.push(...referencedV3Ids(cfg.teamIds));
        geofenceIds.push(...referencedV3Ids(recipients.geofences));
        if (cfg.followUpScenarioId) scenarioIds.push(...referencedV3Ids([cfg.followUpScenarioId]));
    }
    return {
        deviceIds: uniqueIds(deviceIds),
        teamIds: uniqueIds(teamIds),
        geofenceIds: uniqueIds(geofenceIds),
        scenarioIds: uniqueIds(scenarioIds),
    };
}

/** The scenario's V3 recipients with no uuid in `idMaps`, per entity type. */
export function getUnresolvedV3Recipients(scenario, idMaps = {}) {
    const out = { devices: [], teams: [], geofences: [] };
    for (const action of actionList(getField(scenario, 'actions'))) {
        const type = toV4ActionType(getField(action, 'type') || '');
        const missing = unresolvedRecipientIds(type, getField(action, 'config'), idMaps);
        Object.keys(out).forEach(kind => out[kind].push(...missing[kind]));
    }
    return {
        devices: uniqueIds(out.devices),
        teams: uniqueIds(out.teams),
        geofences: uniqueIds(out.geofences),
    };
}
